import type { SegmentedControlOption } from '@/components/ui';
import { DASHBOARD_CURRENCY, FUNNEL_METRICS, type FunnelMetric } from '@/lib/targets';
import type { ContributionRoll } from '@/lib/team-contribution';
import type { ActualsState, TeamSubject } from '@/lib/use-team-performance';
import type { TargetFxMode, User } from '@/lib/types';

/**
 * THE PIECES THE FINANCIAL ANALYTICS TAB SHARES BETWEEN ITS TWO PANELS.
 *
 * ⚠️ **This folder is the TEAM half of the deleted Management Dashboard, moved
 * intact.** Every rule below was learned from a defect on that page, and the
 * ones marked with a warning are load-bearing rather than stylistic. The
 * redesign changed the LAYOUT and nothing about what a figure is allowed to
 * claim.
 *
 * ⚠️ **A PLAIN `.ts` MODULE, DELIBERATELY.** `AttributionNote` is the tab's one
 * other shared piece and it sits in its own file: a module that exports a
 * component alongside constants trips `react(only-export-components)` once per
 * constant, which is ten warnings none of which are about the component.
 *
 * ── ⚠️ WHAT THIS TAB IS ALLOWED TO CLAIM ───────────────────────────────────
 *
 * Four states are one coalesce away from each other and every one of them is a
 * statement about somebody's performance:
 *
 *   `target: null`   nobody committed a figure
 *   `actual: null`   the figure CANNOT BE COMPUTED — an unattributable group
 *   `actual: '0.00'` a real answer: nothing closed in this window
 *   a 403            you may not ask
 *
 * **None of them may render as `0`, as an empty bar, or as a dash that reads
 * like a figure.** `TargetTiles` enforces that for the tiles; the contribution
 * table enforces it for the percentages, and `team-contribution.ts` is where the
 * arithmetic refuses rather than dividing.
 *
 * ── 🔴 THE PER-PERSON HALF IS ADMIN-ONLY AND THE TEAM HALF IS NOT ───────────
 *
 * `/targets/actuals` refuses `subjectType: 'user'` naming anybody but the caller
 * without the targets audit. Group and organisation subjects carry no refusal at
 * all. So a manager who is not an administrator sees the team's figures and a
 * refusal where the breakdown would be — rendered in the `warning` register
 * rather than the `danger` one, because nothing is broken.
 */

/* -------------------------------------------------------------------------
 * The currency
 * --------------------------------------------------------------------- */

/**
 * The currencies this tab will report in.
 *
 * 🔴 **THE GBP-ONLY RULE OF 2026-09-02 IS WITHDRAWN — owner, 2026-09-11.**
 * *SUPERSEDED: "this dashboard is single-currency … `DASHBOARD_CURRENCY` is
 * passed to every formatter".* Measured on production the same day:
 *
 * ```
 * invoices by currency     USD 652   EUR 89   GBP 56
 * collected (FY 2025-26)   GBP 312,417.20
 * excludedByCurrency       234 paid invoices dropped from that figure
 * ```
 *
 * So the tiles were reporting roughly 5% of the business with no unit printed on
 * the number. The owner chose a switcher over leaving it GBP-only.
 *
 * ⚠️ **Three codes, frozen here, and NOT read off `f_currencies`.** The master
 * holds 164 rows and the server sums against ONE base currency per request — a
 * picker offering all 164 would let somebody ask for a currency in which every
 * figure is zero and every document excluded, which reads as a dead business
 * rather than as a question nobody should have been offered.
 */
/**
 * ⚠️ **ORDERED USD, EUR, GBP — the same order the other three tabs offer**, so
 * the control is in the same place on every tab of one dashboard. That order is
 * most-used-first and measured: 652 USD invoices, 89 EUR, 56 GBP.
 *
 * 🔴 **THE ORDER MATCHES AND THE DEFAULT DELIBERATELY DOES NOT.** This tab
 * opens on **GBP** while the three revenue tabs open on USD, and that is not an
 * oversight to harmonise: **a TARGET is denominated**, every target in
 * `f_revenue_targets` is held in GBP, and these tiles compare a target against
 * an actual. Opening on USD would put a GBP target beside a USD actual on the
 * first render — a comparison that is arithmetically meaningless, and the exact
 * confusion the switcher was added to remove. The revenue tabs have no target
 * to be denominated against, so they open where the money is.
 *
 * See `DEFAULT_ANALYTICS_CURRENCY` below, which is `DASHBOARD_CURRENCY` and
 * stays that way.
 */
export const ANALYTICS_CURRENCIES: readonly string[] = ['USD', 'EUR', 'GBP'];

/**
 * The same three, shaped for a `SegmentedControl`.
 *
 * ⚠️ **A SEGMENTED CONTROL AND NOT A `Select`, TO MATCH THE OTHER THREE TABS.**
 * *SUPERSEDED: a `Select` with three options.* It worked, and it made this one
 * tab of a four-tab dashboard operate differently from its siblings — the
 * owner's brief was that these read as ONE dashboard.
 *
 * The reason the siblings chose segmented is worth keeping rather than
 * inheriting blindly: **the currency is the UNIT of every figure on the tab**,
 * and a collapsed dropdown shows the reader which unit they are in only after
 * they look for it. All three codes visible at once means a figure can never be
 * quoted in the wrong currency by somebody who did not think to check.
 */
export const ANALYTICS_CURRENCY_OPTIONS: readonly { value: string; label: string }[] =
  ANALYTICS_CURRENCIES.map((code) => ({ value: code, label: code }));

/**
 * The currency a bare tab opens on.
 *
 * ⚠️ **It is `DASHBOARD_CURRENCY`, so nothing changes for anybody until they
 * touch the control.** Read from the constant rather than written out again:
 * the server's own default is what makes "send no parameter" and "send GBP" the
 * same request, and a second copy of the code here would be a place for the two
 * to disagree.
 */
export const DEFAULT_ANALYTICS_CURRENCY = DASHBOARD_CURRENCY;

/**
 * HOW OTHER CURRENCIES ARE CONVERTED (owner 2026-10-28).
 *
 * `document` converts each document at its own date (the long-standing
 * behaviour, and what an absent `fxMode` key means). `period_end` converts
 * through GBP at the period-end rate, the way Xero's P&L translates.
 *
 * ⚠️ **`period_end` is the default (owner 2026-10-28)**, superseding the
 * earlier "`document`, so nothing changes until touched" rule: a bare panel
 * now opens on Xero's basis. A choice the person already made on the control
 * (the remembered state) still wins. Because an absent `fxMode` means
 * `document` on the wire, the hooks send the key for `period_end` and omit it
 * only for `document`. Commissions ("Targets & commission") stay on document
 * dates whatever this says.
 */
export const DEFAULT_FX_MODE: TargetFxMode = 'period_end';

export const FX_MODE_OPTIONS: readonly SegmentedControlOption<TargetFxMode>[] = [
  { value: 'document', label: 'Document date' },
  { value: 'period_end', label: 'Period end' },
];

/** Narrows a remembered value; anything else falls back to the default. */
export function parseFxMode(raw: unknown): TargetFxMode | undefined {
  return raw === 'document' || raw === 'period_end' ? raw : undefined;
}

/**
 * 🔴 **THE CURRENCY THE ACTUALS WERE ACTUALLY SUMMED IN — AND IT IS NOT THE
 * PICKER'S STATE.**
 *
 * ⚠️ **`row.baseCurrency` IS THE WRONG FIELD AND IT LOOKS LIKE THE RIGHT ONE.**
 * `targets.ts` records the measurement: that field is the stored TARGET's
 * currency, which is a different fact and diverges the moment somebody holds a
 * target in another currency. Formatting an ACTUAL with it is wrong in exactly
 * the case a multi-currency dashboard exists to describe.
 *
 * ✅ **RESOLVED 2026-09-11 — the field is `measuredInCurrency`**, top level on
 * the actuals response, shipped with the `baseCurrency` query parameter.
 *
 * ⚠️ **IT IS THE SERVER'S ANSWER, NEVER THE PICKER'S STATE**, and that is the
 * whole point of reading it: asking for USD and being answered in GBP is
 * reachable — an older API that predates the parameter simply omits it and sums
 * in GBP, because that was the only thing it could do. Returning the reader's
 * CHOICE here would relabel a GBP figure as USD, a wrong number wearing a
 * plausible symbol, which is strictly worse than the understatement this whole
 * change exists to fix.
 *
 * ⚠️ **The fallback is `DASHBOARD_CURRENCY` and it is not a guess:** an absent
 * field means a server from before the parameter existed, and that server summed
 * in GBP by construction.
 */
export function actualsCurrency(state: ActualsState): string {
  return state.measuredInCurrency ?? DASHBOARD_CURRENCY;
}

/**
 * What a figure LEFT OUT, worded once.
 *
 * 🔴 **THE PREMISE UNDER THIS DUPLICATE IS FALSE AS OF 2026-09-11, RE-MEASURED
 * 2026-09-13. THE LIBRARY FUNCTION WAS RESTORED AND THIS PAIR IS NOW A GENUINE
 * SECOND COPY.**
 *
 * *SUPERSEDED: "⚠️ **`excludedByCurrencyNote` IN `@/lib/targets` IS THE CANONICAL
 * HOME AND IT RETURNS `null` UNCONDITIONALLY.** It was neutered on 2026-09-02 by
 * the GBP-only ruling, and its own comment says it is 'the one place that has to
 * change' if multi-currency dashboards come back. They have. **Calling it here
 * would render nothing while looking exactly like working code** — this repo's
 * most-shipped defect — so this local copy exists to make the count visible
 * today."*
 *
 * Measured in `src/lib/targets.ts`: `excludedByCurrencyNote` opens
 * *"✅ **RESTORED 2026-09-11. THE GBP-ONLY RULING THAT NEUTERED THIS IS
 * WITHDRAWN.**"* and returns a real note. **The justification above expired two
 * days before this was read**, which is the shape this repo keeps recording — a
 * duplicate outliving the outage it was built around, because nothing fails when
 * the original comes back and nobody re-reads the comment that says why the copy
 * exists.
 *
 * ⛔ **DELIBERATELY NOT DELETED IN THIS PASS, AND THE REASONS ARE NOT
 * BUREAUCRATIC — THE TWO ARE NOT INTERCHANGEABLE TODAY.** Measured 2026-09-13:
 *
 *   `short`    **byte-identical.** `N in another currency`, both.
 *
 *   `detail`   **materially different.** This one states a FACT — *"Excludes N
 *              documents in another currency. Nothing converts them."* The
 *              library's now ends *"Switch the currency above to see them"*,
 *              which is an INSTRUCTION naming a control. That control exists on
 *              this tab; it does not exist everywhere the library is called, and
 *              the fact-not-instruction rule below was a decision rather than a
 *              style. Swapping changes what a `title` tells a reader to do.
 *
 *   the guard  **this copy is STRICTER.** `!Number.isFinite(excluded)` catches a
 *              `NaN`; the library's `excluded === null || excluded <= 0` does
 *              not, and `NaN <= 0` is `false` — so a `NaN` reaching the library
 *              renders **"NaN in another currency"** under a figure. That is a
 *              real regression hiding inside a "one-line import swap".
 *
 * 🔴 **AND THE BACKEND IS CONCURRENTLY CHANGING WHAT THE COUNT MEANS** — from
 * *"in another currency"* to *"could not be converted"* — so both wordings are in
 * play and unifying them now would have to be undone. **Follow-up, once that
 * lands: delete this pair, import the library's, and carry the `Number.isFinite`
 * guard INTO the library rather than losing it.** Do not let the two wordings
 * drift further in the meantime, and do not extend this one — extend the
 * library's.
 *
 * ⚠️ **THE SHAPE MIRRORS `ExcludedNote` EXACTLY** (`short` for a dense cell,
 * `detail` for a `title` or a roomy strip), which is what keeps that follow-up
 * cheap.
 *
 * ⚠️ **`0` PRINTS NOTHING, and that is the guard rather than tidiness.** An
 * "0 excluded" line on all five tiles on every window is the noise that teaches
 * somebody to stop reading the small print under a figure — at which point the
 * one time it says 234 is the one time it is not read.
 *
 * ⚠️ **`null` is NOT zero.** `null` means nothing was computed at all; `0` is a
 * measurement meaning no document was excluded. Both render nothing here, for
 * different reasons, and neither may be coalesced into the other upstream.
 *
 * ⚠️ **NO CURRENCY IS NAMED — still right, and the REASON WRITTEN HERE DIED ON
 * 2026-09-11.**
 *
 * *SUPERSEDED: "The exclusion is taken against the currency the SUM was made in,
 * **which is not on the wire yet** — see `actualsCurrency` above. *'in another
 * currency'* is the strongest true statement available until it is."*
 *
 * It is on the wire: `measuredInCurrency`, which `actualsCurrency` sixty lines
 * above reads and which that function's own comment marks *"✅ RESOLVED
 * 2026-09-11"*. **So this block asserted the opposite of its own file**, and the
 * clause counselled waiting for something that had already arrived.
 *
 * **The behaviour does not change, for a reason that had to be re-derived rather
 * than inherited:** the count is of documents in the currencies that were left
 * OUT — several, unknown, and none of them the one now on the wire. The field
 * names the currency the sum was made IN, which is a different fact, so it could
 * only ever support a different sentence (*"excluded from the GBP figure"*).
 * *"in another currency"* remains the strongest true statement, and it is now
 * true for good rather than until a field ships.
 *
 * ⚠️ **Right answer, dead reason, nothing failing** — so nothing was ever going
 * to correct this from the code. The sentence the backend is moving to
 * (*"could not be converted"*) is the next thing to land on it; see the
 * follow-up above.
 */
/*
 * ✅ **DELETED 2026-09-13 — `excludedByCurrencyNote` IN `@/lib/targets` IS THE
 * ONE COPY AGAIN, AND THIS IS THE FOLLOW-UP THE BLOCK ABOVE PRESCRIBED.**
 *
 * That block named its own exit condition — *"once that lands: delete this pair,
 * import the library's, and carry the `Number.isFinite` guard INTO the library
 * rather than losing it"* — and the thing it was waiting for has landed: backend
 * migration 178 moved the count's meaning from *"in another currency"* to
 * *"could not be converted"*, so both wordings were no longer in play and the
 * reason to keep two was spent.
 *
 * **All three of its objections were discharged rather than overruled**, which is
 * the only honest way to close a divergence this file argued for at length:
 *
 *   the guard   **CARRIED IN, not lost.** `@/lib/targets` now tests
 *               `!Number.isFinite(excluded)`, so the `NaN` that rendered
 *               "NaN in another currency" under a money figure is caught there.
 *
 *   `detail`    **RESOLVED IN THIS COPY'S FAVOUR.** The divergence was that the
 *               library's ended *"Switch the currency above to see them"* — an
 *               INSTRUCTION naming a control that does not exist everywhere it is
 *               called. Under the new meaning that instruction does not WORK
 *               either: a document with no rate for its date is missing from
 *               every currency the switcher offers. The library states a fact now,
 *               which is what this copy always did.
 *
 *   `short`     was byte-identical, so there was nothing to reconcile.
 *
 * ⛔ **DO NOT REINTRODUCE A LOCAL COPY.** This is a sentence about MONEY and what
 * a figure left out; two copies agree the day they are written and drift on the
 * first correction — which is exactly what happened here, and the drift outlived
 * the outage it was built around by two days because nothing fails when a
 * duplicate goes stale. The two consumers on this tab import the library's
 * directly.
 */

/* -------------------------------------------------------------------------
 * The team
 * --------------------------------------------------------------------- */

export const WHOLE_ORGANISATION: TeamSubject = { kind: 'organisation' };

/**
 * The team this tab OPENS on — owner's instruction, 2026-09-02.
 *
 * ⚠️ **A SLUG, AND NEVER AN ID.** The four seeded groups happen to run 1–4 in
 * slug order, which is exactly the coincidence that makes a hardcoded id look
 * right; the ids are production's counter and a group somebody adds tomorrow
 * gets 5 with no relationship behind it. The slug is the server's own join key —
 * it is what `RELATIONSHIP_BY_GROUP_SLUG` attributes on — and it is
 * write-on-create-only server-side, so it cannot be renamed out from under this.
 * The group's NAME is editable wording and must never be matched on.
 *
 * ⚠️ **It is a DEFAULT, not a rule.** If no active group carries this slug the
 * tab stays on the whole organisation rather than opening on nothing — an absent
 * group is a real state (the master is admin-editable and `isActive` retires
 * rows), and a dashboard that opened on an empty team would read as broken
 * rather than as unconfigured.
 */
export const DEFAULT_TEAM_SLUG = 'account_lead';

/** A team's identity in a `<select>`. */
export function teamValue(subject: TeamSubject): string {
  return subject.kind === 'organisation' ? 'org' : String(subject.groupId);
}

/** What the reader calls this team. */
export function teamLabel(subject: TeamSubject): string {
  return subject.kind === 'organisation' ? 'Whole organisation' : subject.name;
}

/** A person's display name, defensively — `name` can be `''` on a staff record. */
export function personName(user: Pick<User, 'id' | 'name' | 'email'>): string {
  return user.name.trim() || user.email.trim() || `Staff #${user.id}`;
}

/**
 * A percentage as text, to one decimal place.
 *
 * ⚠️ **Never clamped, at either end.** 120% is the interesting number when a
 * target is beaten and a negative is the truth when it is not — only the BAR's
 * geometry is clamped, in `ShareBar`. And `null` prints an em dash, which is
 * "no figure" and never "zero".
 */
export function pctText(pct: number | null): string {
  if (pct === null || !Number.isFinite(pct)) return '—';
  return `${Math.round(pct * 10) / 10}%`;
}

/* -------------------------------------------------------------------------
 * The contribution table's shared types
 * --------------------------------------------------------------------- */

/**
 * One roll-up per FUNNEL metric — the table renders these columns side by side.
 *
 * ⚠️ **Keyed by `FunnelMetric` and not by `TargetMetric`.** `booked` is still a
 * settable target and is no longer on the funnel, so a map claiming to hold it
 * would hand back `undefined` from a property the type says is present. See
 * `FunnelMetric`.
 */
export type MetricRolls = Record<FunnelMetric, ContributionRoll>;

/**
 * The metric whose roll stands in for the TEAM-LEVEL facts.
 *
 * ⚠️ **Only ever read for things that are the same across every column** — the
 * member list and its order, and the attribution-derived `unattributable` /
 * `incomparable` basis. Every one of those comes from the subject rather than
 * from the metric, so which member of `FUNNEL_METRICS` this names cannot change
 * the answer. **Never read a target, an actual or a percentage off it.**
 *
 * Derived from `FUNNEL_METRICS` rather than written out, so a reordering of that
 * list cannot leave this naming a metric that is no longer first. It is a tuple,
 * so index 0 is a real member and needs no fallback.
 */
export const FIRST_METRIC: FunnelMetric = FUNNEL_METRICS[0];

/**
 * Whether a member's cell leads with the money or with the share.
 *
 * ⚠️ **NEITHER MODE HIDES THE OTHER FIGURE — it only decides which one is the
 * headline.** A percentage whose amount is off screen is the thing people argue
 * about in a review, and an amount with no share does not answer "is this person
 * carrying the team". Both stay in the cell; the switch moves the emphasis, so
 * nothing a reader was looking at can disappear by touching a control they did
 * not understand.
 */
export type FigureMode = 'value' | 'percentage';

export const FIGURE_MODE_OPTIONS: readonly SegmentedControlOption<FigureMode>[] = [
  { value: 'value', label: 'Values' },
  { value: 'percentage', label: 'Percentage' },
];

/* -------------------------------------------------------------------------
 * Reading the table
 * --------------------------------------------------------------------- */

/**
 * WHO CONTRIBUTED NOTHING AT ALL — the members a reader may collapse away.
 *
 * Measured on production, 2026-09-11: four of the account-lead team's members
 * render `0.00` in all five columns, which is four rows of nothing in the
 * middle of the analytical half of this tab. They are HIDDEN BY DEFAULT and
 * they are **not deleted** — *"nobody on this team contributed"* and *"the table
 * only lists contributors"* are different statements about a team, so the
 * control that hides them has to say how many it hid.
 *
 * 🔴 **A ZERO AND A NON-ANSWER ARE DIFFERENT, AND ONLY THE ZERO MAY BE
 * HIDDEN.** This is the whole reason the test below is written out per member
 * per metric rather than as `Number(actual) === 0` over one column. Four states
 * reach a cell and three of them must survive the toggle:
 *
 *   `status: 'refused'`   you may not read this person   **stays**
 *   `status: 'error'`     we could not find out          **stays**
 *   `actual: null`        the figure is not computable   **stays**
 *   `actual: '0.00'`      a real answer: nothing closed  **hidable**
 *
 * A refusal collapsed under "no activity" is this dashboard telling somebody a
 * colleague did nothing when what actually happened is that nobody asked.
 *
 * ⚠️ **A MEMBER CARRYING AN EXCLUSION IS NOT SILENT EITHER, AND THAT ONE IS
 * EASY TO MISS.** A person whose figure reads `0.00` *because* fourteen of their
 * documents were billed in another currency has a real note under that zero —
 * and `excludedByCurrency` is the one thing on this tab that must never be
 * hidden, because it is what stops the currency switcher understating the
 * business in silence. So a non-zero exclusion keeps the row on screen even
 * though the number beside it is a genuine zero.
 *
 * ⚠️ **EVERY metric must agree.** A member who closed nothing on four metrics
 * and one deal on the fifth is a contributor; collapsing them would hide the
 * only figure they have.
 *
 * ⚠️ **Walked by INDEX, like the table itself.** Every roll carries the same
 * member list in the same order — they are built from one `membersState` — and
 * a metric whose row is missing at an index is treated as "not silent", because
 * an absent row is an unknown rather than a zero.
 *
 * 🔴 **A COLUMN NOBODY WAS MEASURED ON AT ALL IS NOT EVIDENCE ABOUT ANYBODY,
 * AND IT IS SKIPPED — 2026-09-11.** The funnel names `invoiced` before the
 * server returns it, so during that deploy window the whole column is unknown:
 * the team row is absent and every member's is. Counted as an unknown PER
 * MEMBER it would make nobody silent and un-collapse a table that had settled
 * — a visible change with no cause a reader could see. Counted as a column it
 * says nothing, which is the truth.
 *
 * ⚠️ **An EMPTY informative list means nobody is silent, never everybody.**
 * `[].every(...)` is `true`, so without the guard an unattributable group —
 * every column unknown — would collapse its entire membership out of the table
 * and report "show 6 members with no activity" about people nothing was
 * measured for.
 */
export function silentMemberIds(rolls: MetricRolls): ReadonlySet<number> {
  const silent = new Set<number>();
  const roster = rolls[FIRST_METRIC].members;

  const informative = FUNNEL_METRICS.filter(
    (metric) =>
      rolls[metric].teamActual !== null ||
      rolls[metric].members.some((row) => row.actual !== null),
  );
  if (informative.length === 0) return silent;

  roster.forEach((member, index) => {
    const quiet = informative.every((metric) => {
      const row = rolls[metric].members[index];
      if (row === undefined) return false;
      // Anything but a completed read is a state, not a figure.
      if (row.status !== 'ok') return false;
      if (row.actual === null) return false;
      if ((row.excludedByCurrency ?? 0) > 0) return false;
      const amount = Number(row.actual);
      return Number.isFinite(amount) && amount === 0;
    });
    if (quiet) silent.add(member.userId);
  });

  return silent;
}

/**
 * Whether anything at all was measured, across every metric and every member.
 *
 * ⚠️ **It gates the CURRENCY in a panel's description line, and that is the
 * whole job.** Naming a unit over a panel that measured nothing is a claim
 * about a currency no figure was summed in — the same overstatement the
 * measured-in strip is suppressed for on a loading or refused tab.
 */
export function hasAnyFigure(rolls: MetricRolls): boolean {
  return FUNNEL_METRICS.some(
    (metric) =>
      rolls[metric].teamActual !== null ||
      rolls[metric].members.some((member) => member.actual !== null),
  );
}

/**
 * A panel's one description line: what is counted, whose it is, what unit.
 *
 * ⚠️ **A reader must never have to infer what a figure counts**, and on this
 * dashboard three of the four tabs report money that does not reconcile with
 * the others — so the population and the unit travel with the heading rather
 * than being left to the control that set them. `null` parts are dropped, which
 * is how the unit disappears on a panel that has measured nothing rather than
 * rendering an empty segment between two separators.
 */
export function panelDescription(parts: readonly (string | null)[]): string {
  return parts.filter((part): part is string => part !== null && part !== '').join(' · ');
}
